<template>
  <a-config-provider
    :locale="zh_CN"
    :auto-insert-space-in-button="false"
  >
    <a-spin
      class="preview-app"
      :class="{
        'preview-app--collapsed': menuCollapsed,
        'preview-app--initial': !initialized
      }"
      :spinning="loading || !initialized"
      size="large"
    >
      <record-dialog />

      <span
        v-if="!initialized"
        slot="indicator"
      />
      <!-- well known filters -->
      <svg
        width="0"
        height="0"
        style="display: block"
      >
        <defs>
          <filter id="filtersharpen">
            <feConvolveMatrix kernelMatrix="-2 -2 -2 -2 32 -2 -2 -2 -2" />
          </filter>
          <filter id="filtercontour">
            <feConvolveMatrix
              kernelMatrix="-1 -1 -1 -1 8 -1 -1 -1 -1"
              divisor="1"
              bias="1"
            />
          </filter>
          <filter id="filteredgeEnhance">
            <feConvolveMatrix kernelMatrix="-1 -1 -1 -1 10 -1 -1 -1 -1" />
          </filter>
          <filter
            id="filterdark"
            primitiveUnits="objectBoundingBox"
          >
            <feImage
              href="/preview/icon/filter/dark.png"
              x="0"
              y="0"
              width="100%"
              height="100%"
              preserveAspectRatio="none"
              result="image"
            />
            <feBlend
              in="SourceGraphic"
              in2="image"
              mode="multiply"
            />
          </filter>
          <filter
            id="filterlight"
            primitiveUnits="objectBoundingBox"
          >
            <feImage
              href="/preview/icon/filter/light.jpg"
              x="0"
              y="0"
              width="100%"
              height="100%"
              preserveAspectRatio="none"
              result="image"
            />
            <feBlend
              in="SourceGraphic"
              in2="image"
              mode="multiply"
            />
          </filter>
          <filter
            id="filtermagma"
            primitiveUnits="objectBoundingBox"
          >
            <feImage
              href="/preview/icon/filter/magma.jpg"
              x="0"
              y="0"
              width="100%"
              height="100%"
              preserveAspectRatio="none"
              result="image"
            />
            <feBlend
              in="SourceGraphic"
              in2="image"
              mode="multiply"
            />
          </filter>
        </defs>
      </svg>
      <Header />
      <main class="preview-main">
        <info-mask />
        <OperationList />
        <el-tooltip
          manual
          content="请选择素材进行更换"
          :value="showTooltip"
          placement="right-start"
          :offset="86"
          popper-class="material-tooltip"
        >
          <div
            ref="menu"
            class="preview-app__menu"
            :class="{ 'preview-app__menu--top': showTooltipMarker }"
          >
            <transition
              mode="out-in"
              name="menu"
            >
              <filter-menu
                v-if="filterShowing"
                :key="selectedEffectId"
              />
              <component
                :is="currentMenu"
                v-else
                :scroll-to="scrollTo"
              />
            </transition>
          </div>
        </el-tooltip>
        <transition name="sub-menu">
          <div
            v-show="showSubmenu"
            class="fragment-sub-menu"
          >
            <SubVideoClipList v-show="showSubVideoClipList" />
            <TransitionList v-show="showTransitionList" />
          </div>
        </transition>
        <video
          v-if="previewTemplate"
          ref="materialVideo"
          :src="previewTemplate.qurl"
          :autoplay="true"
          class="template-preview-material"
          controls="controls"
        />
        <el-tooltip
          placement="right"
          :content="menuCollapsed ? '展开' : '收起'"
        >
          <button
            type="button"
            class="menu-collapse-btn"
            :class="{
              'menu-collapse-btn--sub': showSubmenu
            }"
            @click="toggleMenuCollapse"
          >
            <svg
              width="5"
              height="12"
              viewBox="0 0 5 12"
              class="menu-collapse-btn__svg"
            >
              <path
                class="menu-collapse-btn__path"
                :d="
                  menuCollapsed
                    ? 'M4.73 6L1.87 1.04A1 1 0 0 0 .13 2.04L2.42 6L.13 9.96A1 1 0 0 0 1.87 10.96z'
                    : 'M2.58 6L4.87 2.04A1 1 0 0 0 3.13 1.04L.27 6L3.13 10.96A1 1 0 0 0 4.87 9.96z'
                "
              />
            </svg>
          </button>
        </el-tooltip>
        <div
          class="app-player-format"
          :style="{ paddingBottom: timelineHeight - 1 + 'px' }"
        >
          <div
            ref="playerArea"
            class="app-player"
            @mousewheel="handleMouseWheel"
            @mousedown.self="handleMouseDown"
          >
            <timeline-player />
          </div>
          <div class="app-player-format__actions">
            <el-tooltip
              :content="`背景音乐: ${setupMusic ? '开' : '关'}`"
              placement="right"
            >
              <button
                type="button"
                :class="[
                  'app-player-format__action',
                  { 'app-player-format__action--active': setupMusic }
                ]"
                @click="openBackgroundAudio"
              >
                <span class="icon iconfont">&#xe65f;</span>
              </button>
            </el-tooltip>
            <el-tooltip
              :content="`视频脚本: ${usedVideoScriptId ? '开' : '关'}`"
              placement="right"
            >
              <button
                v-if="!isFisher"
                type="button"
                :class="[
                  'app-player-format__action app-player-format__action--small',
                  { 'app-player-format__action--active': usedVideoScriptId }
                ]"
                @click="toggleVideoScriptPeek"
              >
                <a-icon :component="icons.tool" />
              </button>
            </el-tooltip>
            <el-tooltip
              :content="allMuted ? '启用视频原声' : '关闭视频原声'"
              placement="right"
              :disabled="!canMute"
            >
              <button
                :class="[
                  'app-player-format__action',
                  { 'app-player-format__action--active': canMute && allMuted }
                ]"
                :disabled="!canMute"
                @click="muteAll"
              >
                <a-icon :component="icons.mute" />
              </button>
            </el-tooltip>
            <div class="app-player-format__action-divider" />
            <el-tooltip
              content="显示网格"
              placement="right"
            >
              <button
                :class="[
                  'app-player-format__action app-player-format__action--small',
                  { 'app-player-format__action--active': gridShowing }
                ]"
                type="button"
                @click="toggleGridShow"
              >
                <a-icon :component="icons.grid" />
              </button>
            </el-tooltip>
            <el-tooltip
              :content="snapEnabled ? '画板吸附: 开' : '画板吸附: 关'"
              placement="right"
            >
              <button
                :class="[
                  'app-player-format__action app-player-format__action--small',
                  { 'app-player-format__action--active': snapEnabled }
                ]"
                type="button"
                @click="toggleSnapEnable"
              >
                <a-icon :component="icons.snap" />
              </button>
            </el-tooltip>
          </div>
          <div class="app-info-component-wrapper">
            <transition
              mode="out-in"
              name="sub-menu"
            >
              <keyboard-shortcuts v-if="keyboardShortcutShowing" />
              <multiple-selection-format v-else-if="multipleSelected" />
              <audio-format
                v-if="selectedAudioId"
                :key="selectedAudioId"
              />
              <component
                :is="currentSubmenu"
                v-else
                :key="(currentEffect && currentEffect.id) || ''"
              />
            </transition>
          </div>
        </div>
        <timeline-view
          v-show="showTimeline"
          class="app-timeline"
        />
      </main>
      <preview-player />
      <template-player />
      <template-preview-modal />
      <preview-settings />
      <rename-modal />
      <context-menu />
      <meal-auth-dialog />
      <material-upload />
      <subtitle-menu />
      <video-script-menu />
      <save-preset />
      <link-material />
      <video-series-tag />
      <text-video-series-tag />
      <video-script-peeker />
      <video-script-previewer />
      <material-color-picker />
      <inline-image-cropper />
      <div
        v-show="selectionDrawing"
        class="preview-app__cover"
        :style="coverAreaStyle"
      />
    </a-spin>
  </a-config-provider>
</template>

<script>
import Header from './components/header/Header'
import OperationList from './components/operationList/OperationList'
import VideoClipList from './components/video/VideoClipList'
import SubVideoClipList from './components/video/SubVideoClipList'
import VideoFormat from './components/video/VideoFormat'
import BackgroundAudioFormat from './components/music/BackgroundAudioFormat'
import MusicMenu from './components/music/MusicMenu'
import MaterialMenu from './components/material/MaterialMenu'
import TextMenu from './components/text/TextMenu'
import TextFormat from './components/text/TextFormat'
import TimelineView from './components/timeline/TimelineView'
import MaterialInfo from './components/material/MaterialInfo'
import VideoMaterialInfo from './components/material/VideoMaterialInfo'
import StickerInfo from './components/material/StickerInfo'
import LottieInfo from './components/material/LottieInfo'
import StampMenu from './components/stamp/StampMenu'
import ImageStampInfo from './components/stamp/ImageStampInfo'
import TextStampInfo from './components/stamp/TextStampInfo'
import StyleMenu from './components/style/StyleMenu'
import StyleInfo from './components/style/StyleInfo'
import SubtitleMenu from './components/subtitle/SubtitleMenu'
import SubtitleFormat from './components/subtitle/SubtitleFormat'
import ShapeMenu from './components/shape/ShapeMenu'
import ShapeFormat from './components/shape/ShapeFormat'
import ContainerFormat from './components/shape/ContainerFormat'
import TextContainerFormat from './components/shape/TextContainerFormat'
import GroupFormat from './components/shape/GroupFormat'
import WordArtFormat from './components/text/WordArtFormat'
import IconFormat from './components/material/IconFormat'
import TemplateMenu from './components/template/TemplateMenu'
import FigureMenu from './components/digitalHuman/FigureMenu.vue'
import FigureTemplateMenu from './components/digitalHuman/TemplateMenu.vue'
import FilterMenu from './components/material/FilterMenu'
import TimelinePlayer from './components/player/TimelinePlayer'
import PreviewPlayer from './components/player/PreviewPlayer'
import TemplatePlayer from './components/player/TemplatePlayer'
import TransitionList from './components/video/TransitionList'
import PreviewSettings from './components/previewSettings/PreviewSettings'
import MultipleSelectionFormat from './components/video/MultipleSelectionFormat'
import KeyboardShortcuts from './components/video/KeyboardShortcuts'
import AudioFormat from './components/music/AudioFormat'
import InfoMask from './components/base/InfoMask'
import RenameModal from './components/base/RenameModal'
import MealAuthDialog from './components/base/mealAuthDialog'
import ContextMenu from './components/contextMenu/ContextMenu'
import MineMenu from './components/mine/MineMenu'
import MaterialUpload from './components/base/MaterialUpload'
import SavePreset from './components/savePreset/SavePreset'
import LinkMaterial from './components/linkMaterial/LinkMaterial'
import VideoSeriesTag from './components/linkMaterial/VideoSeriesTag'
import TextVideoSeriesTag from './components/linkMaterial/TextVideoSeriesTag'
import VideoScriptMenu from './components/videoScript/VideoScriptMenu'
import VideoScriptPeeker from './components/videoScript/VideoScriptPeeker'
import VideoScriptPreviewer from './components/videoScript/VideoScriptPreviewer'
import TemplatePreviewModal from './components/template/TemplatePreviewModal'
import MaterialColorPicker from './components/materialColorPicker/MaterialColorPicker'
import InlineImageCropper from './components/material/InlineImageCropper'
import RecordDialog from './components/timeline/Record/RecordDialog'
import { clamp, isMac } from './utils'
import initWebSocket from './utils/webSocket.js'
import { mapGetters, mapMutations, mapState } from 'vuex'
import zh_CN from 'ant-design-vue/lib/locale-provider/zh_CN'
import { message, Modal } from 'ant-design-vue'
import { get, post } from './utils/api'
import Cookies from 'js-cookie'
import axios from 'axios'

const icons = {
  tool: {
    render (h) {
      return h('svg',
        {
          attrs: { viewBox: '0 0 18 18' }
        },
        [
          h('path', {
            attrs: {
              d: 'M 0.622612 4.23828 L 2.89459 5.95054 L 1.97508 6.18316 L 0.287428 8.2779 L 2.61117 8.2779 L 4.36684 6.0987 L 6.35315 6.09788 L 4.59674 8.2779 L 6.92045 8.2779 L 8.67768 6.09689 L 10.7035 6.09604 L 8.94569 8.2779 L 11.2694 8.2779 L 13.028 6.09503 L 15.0462 6.09418 L 13.2869 8.2779 L 15.6106 8.2779 L 17.3707 6.09319 L 18 6.09319 L 18 16.5325 C 18 17.6371 17.1046 18.5325 16 18.5325 L 16 18.5325 L 2 18.5325 C 0.895431 18.5325 -9.47177e-015 17.6371 -9.60704e-015 16.5325 L -9.60704e-015 16.5325 L -9.60704e-015 4.38068 L 0.0597906 4.38068 L 0.622612 4.23828 Z M 16.1413 13.4905 L 11.3478 13.4905 L 11.3478 14.9763 L 16.1413 14.9763 L 16.1413 13.4905 Z M 10.4918 13.4661 L 1.90761 13.4661 L 1.90761 14.9519 L 10.4918 14.9519 L 10.4918 13.4661 Z M 13.6223 11.347 L 9.68476 11.347 L 9.68476 12.8328 L 13.6223 12.8328 L 13.6223 11.347 Z M 8.97553 11.3226 L 1.93206 11.3226 L 1.93206 12.8085 L 8.97553 12.8085 L 8.97553 11.3226 Z M 4.81871 3.17667 L 7.09071 4.88893 L 5.14061 5.3823 L 2.86861 3.67004 L 4.81871 3.17667 Z M 9.02212 2.11325 L 11.2941 3.8255 L 9.33669 4.32071 L 7.06471 2.60846 L 9.02212 2.11325 Z M 13.1874 1.05945 L 15.4593 2.77171 L 13.5401 3.25728 L 11.2681 1.54503 L 13.1874 1.05945 Z M 17.3751 6.0044e-016 L 17.9237 2.14823 L 17.7054 2.20349 L 15.4334 0.491237 L 17.3751 6.0044e-016 Z'
            }
          })
        ]
      )
    }
  },
  grid: {
    render () {
      return this.$createElement('svg',
        { attrs: { viewBox: '0 0 1024 1024' } },
        [
          this.$createElement('path', {
            attrs: { d: 'M1024 204.8V136.541091h-136.541091V0H819.2v136.541091H546.141091V0h-68.282182v136.541091H204.8V0H136.541091v136.541091H0V204.8h136.541091v273.058909H0v68.282182h136.541091V819.2H0v68.258909h136.541091V1024H204.8v-136.541091h273.058909V1024h68.282182v-136.541091H819.2V1024h68.258909v-136.541091H1024V819.2h-136.541091V546.141091H1024v-68.282182h-136.541091V204.8H1024z m-819.2 0h273.058909v273.058909H204.8V204.8z m0 614.4V546.141091h273.058909V819.2H204.8z m614.4 0H546.141091V546.141091H819.2V819.2z m0-341.341091H546.141091V204.8H819.2v273.058909z' }
          })
        ])
    }
  },
  mute: {
    render () {
      return this.$createElement('svg',
        { attrs: { viewBox: '0 0 20 21' } },
        [
          this.$createElement('path', {
            attrs: { d: 'M3.56115024,2.44175065 L4.38869862,3.87594748 C2.69155722,5.40863076 1.625,7.62639486 1.625,10.0932667 C1.625,14.7186515 5.37461522,18.4682667 10,18.4682667 C10.9004719,18.4682667 11.7677517,18.3261546 12.5806348,18.0631348 L13.4078462,19.4975765 C12.344264,19.8830674 11.1966794,20.0932667 10,20.0932667 C4.4771525,20.0932667 0,15.6161142 0,10.0932667 C0,7.02312707 1.38354106,4.27613072 3.56115024,2.44175065 Z M5.6160254,1.31561428e-13 L16.6160254,19.0525589 L14.8839746,20.0525589 L3.8839746,1 L5.6160254,1.31561428e-13 Z M10,0.0932667397 C15.5228475,0.0932667397 20,4.57041924 20,10.0932667 C20,12.9797482 18.7770369,15.5805914 16.8209977,17.4059094 L15.9824646,15.9542213 C17.462481,14.4437144 18.375,12.3750557 18.375,10.0932667 C18.375,5.46788196 14.6253848,1.71826674 10,1.71826674 C9.27977692,1.71826674 8.58078765,1.80917954 7.91388199,1.98015537 L7.07511977,0.527801972 C8.00026791,0.245267005 8.98238317,0.0932667397 10,0.0932667397 Z M14.9215825,6.05201327 C15.087337,6.05740557 15.2441526,6.12845484 15.3575,6.24951674 C17.4307028,8.46496355 17.3731848,11.9254437 15.2275,14.0707667 C15.1585043,14.1422032 15.0749215,14.1948271 14.9843433,14.2265096 L14.368,13.1592667 L14.500109,13.0223955 C16.0129838,11.3456106 16.0040833,8.7716804 14.44375,7.10451674 C14.3304695,6.9833783 14.2699825,6.82218168 14.2756094,6.6564248 C14.2812362,6.49066793 14.3525153,6.33394427 14.47375,6.22076674 C14.5947262,6.10732781 14.755828,6.04662098 14.9215825,6.05201327 Z M5.60073272,5.97231102 L6.01543433,6.69009718 C5.99255605,6.80291221 5.93828498,6.910697 5.8525,6.99951674 C4.18404047,8.66797963 4.13924632,11.3589469 5.75125,13.0820167 C5.98735172,13.3339967 5.9744799,13.729665 5.72249999,13.9657667 C5.47052008,14.2018685 5.07485173,14.1889966 4.83875,13.9370167 C2.76554721,11.7215699 2.82306518,8.26108979 4.96875,6.11576674 C5.14177819,5.94865052 5.38677924,5.90113036 5.60073272,5.97231102 Z M6.702,7.88226674 L7.36490734,9.03048876 C6.87305035,9.74266694 6.9281015,10.7218855 7.54,11.3757667 C7.7655241,11.6290808 7.74786266,12.0160332 7.50020057,12.24775 C7.25253849,12.4794667 6.86526773,12.4713755 6.6275,12.2295167 C5.47588628,10.9986572 5.50796292,9.07627046 6.7,7.88451674 L6.7,7.88451674 L6.702,7.88226674 Z M13.53125,7.95826674 C14.5958667,9.09549741 14.649658,10.8236514 13.7110323,12.02015 L12.9958573,10.7795349 C13.2522519,10.1258911 13.1284064,9.3561264 12.61875,8.81201674 C12.3836838,8.55990748 12.3971057,8.1650806 12.64875,7.92951674 L12.64875,7.92826674 C12.9008593,7.69320059 13.2956861,7.70662247 13.53125,7.95826674 Z' }
          })
        ])
    }
  },
  snap: {
    render () {
      return this.$createElement('svg',
        { attrs: { viewBox: '0 0 16 16' } },
        [
          this.$createElement('path', {
            attrs: { d: 'M 15.5556 0 L 10.8089 0 C 10.5634 0 10.3644 0.198985 10.3644 0.444444 C 10.3644 0.689904 10.5634 0.888889 10.8089 0.888889 L 15.1111 0.888889 L 15.1111 6.05333 C 15.1111 6.29879 15.3101 6.49778 15.5556 6.49778 C 15.801 6.49778 16 6.29879 16 6.05333 L 16 0.444444 C 16 0.198985 15.801 0 15.5556 0 L 15.5556 0 Z M 5.2 0 L 0.444444 0 C 0.198985 0 0 0.198985 0 0.444444 L 0 6.05333 C 0 6.29879 0.198985 6.49778 0.444444 6.49778 C 0.689904 6.49778 0.888889 6.29879 0.888889 6.05333 L 0.888889 0.888889 L 5.2 0.888889 C 5.44546 0.888889 5.64444 0.689904 5.64444 0.444444 C 5.64444 0.198985 5.44546 0 5.2 0 L 5.2 0 Z M 15.5556 9.50222 C 15.4377 9.50222 15.3246 9.54905 15.2413 9.6324 C 15.1579 9.71575 15.1111 9.82879 15.1111 9.94667 L 15.1111 15.1111 L 10.8089 15.1111 C 10.5634 15.1111 10.3644 15.3101 10.3644 15.5556 C 10.3644 15.801 10.5634 16 10.8089 16 L 15.5556 16 C 15.801 16 16 15.801 16 15.5556 L 16 9.94667 C 16 9.82879 15.9532 9.71575 15.8698 9.6324 C 15.7865 9.54905 15.6734 9.50222 15.5556 9.50222 L 15.5556 9.50222 Z M 5.2 15.1111 L 0.888889 15.1111 L 0.888889 9.94667 C 0.888889 9.70121 0.689904 9.50222 0.444444 9.50222 C 0.198985 9.50222 0 9.70121 0 9.94667 L 0 15.5556 C 0 15.801 0.198985 16 0.444444 16 L 5.2 16 C 5.44546 16 5.64444 15.801 5.64444 15.5556 C 5.64444 15.3101 5.44546 15.1111 5.2 15.1111 L 5.2 15.1111 Z M 12.3289 12.2933 L 12.3289 3.54667 C 12.3289 3.30121 12.1299 3.10222 11.8844 3.10222 C 11.639 3.10222 11.44 3.30121 11.44 3.54667 L 11.44 12.2933 C 11.44 12.5388 11.639 12.7378 11.8844 12.7378 C 12.1299 12.7378 12.3289 12.5388 12.3289 12.2933 L 12.3289 12.2933 Z M 8.72889 12.2311 L 8.72889 3.48444 C 8.72889 3.23898 8.5299 3.04 8.28444 3.04 C 8.03898 3.04 7.84 3.23898 7.84 3.48444 L 7.84 12.2311 C 7.84 12.4766 8.03898 12.6756 8.28444 12.6756 C 8.5299 12.6756 8.72889 12.4766 8.72889 12.2311 L 8.72889 12.2311 Z M 5.04889 12.2311 L 5.04889 3.48444 C 5.04889 3.23898 4.8499 3.04 4.60444 3.04 C 4.35898 3.04 4.16 3.23898 4.16 3.48444 L 4.16 12.2311 C 4.16 12.4766 4.35898 12.6756 4.60444 12.6756 C 4.8499 12.6756 5.04889 12.4766 5.04889 12.2311 L 5.04889 12.2311 Z' }
          })
        ])
    }
  }
}

const loadStart = +new Date()

export default {
  name: 'App',
  components: {
    Header,
    OperationList,
    MusicMenu,
    BackgroundAudioFormat,
    VideoClipList,
    VideoFormat,
    MaterialMenu,
    TextMenu,
    TextFormat,
    MaterialInfo,
    TimelineView,
    VideoMaterialInfo,
    StampMenu,
    ImageStampInfo,
    TextStampInfo,
    StyleMenu,
    StyleInfo,
    SubtitleMenu,
    SubtitleFormat,
    SubVideoClipList,
    TimelinePlayer,
    PreviewPlayer,
    TransitionList,
    PreviewSettings,
    InfoMask,
    ShapeMenu,
    ShapeFormat,
    ContextMenu,
    TemplateMenu,
    TemplatePlayer,
    ContainerFormat,
    StickerInfo,
    LottieInfo,
    TextContainerFormat,
    GroupFormat,
    FilterMenu,
    RenameModal,
    MultipleSelectionFormat,
    MealAuthDialog,
    KeyboardShortcuts,
    MineMenu,
    AudioFormat,
    MaterialUpload,
    SavePreset,
    LinkMaterial,
    VideoSeriesTag,
    TextVideoSeriesTag,
    WordArtFormat,
    VideoScriptMenu,
    TemplatePreviewModal,
    VideoScriptPeeker,
    VideoScriptPreviewer,
    MaterialColorPicker,
    IconFormat,
    InlineImageCropper,
    RecordDialog,
    FigureMenu,
    FigureTemplateMenu
  },
  data () {
    return {
      zh_CN,
      showTooltip: false,
      ownLock: false,
      previewVideo: false,
      icons
    }
  },
  computed: {
    ...mapState([
      'isFisher',
      'fisherInfo',
      'currentIcon', 'stampType', 'timelineHeight', 'showTransitionList', 'showSubVideoClipList',
      'currentMenu', 'currentSubmenu', 'currentMusic', 'setupMusic', 'recordDisabled',
      'previewTemplate', 'filterShowing', 'playerScale', 'gridShowing', 'keyboardShortcutShowing',
      'selectedAudioId', 'usedVideoScriptId', 'videoScriptPeekerVisible', 'effectReordering',
      'userBasics', 'menuCollapsed', 'initialized', 'usedTemplateId', 'stamp', 'selectionDrawing',
      'selectionArea', 'playing', 'snapEnabled'
    ]),
    ...mapGetters(['showSubmenu', 'currentEffect', 'selectedEffectId', 'stampPadding', 'allVideos']),
    selectedEffects () {
      const { selectedEffectIds, effects } = this.$store.state
      return selectedEffectIds.map(id => effects[id])
    },
    multipleSelected () {
      return this.selectedEffects.length > 1
    },
    showTimeline () {
      return this.$store.state.fragments.length !== 0
    },
    showTooltipMarker () {
      return this.$store.getters.revealMask
    },
    playerWidth () {
      return Math.max(833, this.$store.state.screenRatio * 480)
    },
    loading () {
      return this.$store.state.loading
    },
    currentMusicUrl () {
      return this.currentMusic.material_url
    },
    contextMenuVisible () {
      return this.$store.state.contextMenu.visible
    },
    editLock () {
      const {
        effectEditing,
        effectDragging
      } = this.$store.state
      return effectEditing || effectDragging
    },
    params () {
      const {
        screenRatio,
        fragments,
        videoName,
        effects,
        screenHeight,
        myVideoMusic,
        musicStartEnd,
        musicVolume,
        setupMusic,
        stampEnabled,
        stamp,
        ttsRule,
        translationEnabled,
        selectedLanguages,
        useTTSLanguages,
        audios,
        subtitleTracks,
        musicFade,
        usedVideoScriptId,
        languageOptions,
        languageSex,
        useCustomTTS,
        customTTS,
        customTTSIds,
        descKeywords
      } = this.$store.state

      return {
        screenRatio,
        fragments,
        videoName,
        effects,
        screenHeight,
        myVideoMusic,
        musicStartEnd,
        musicVolume,
        setupMusic,
        stampEnabled,
        stamp,
        ttsRule,
        translationEnabled,
        selectedLanguages,
        useTTSLanguages,
        audios,
        subtitleTracks,
        musicFade,
        usedVideoScriptId,
        languageOptions,
        languageSex,
        useCustomTTS,
        customTTS,
        customTTSIds,
        descKeywords
      }
    },
    playerVisible () {
      const { posterVisible, previewVisible, templatePlayerVisible, currentIcon, clippingEffect } = this.$store.state
      return posterVisible || previewVisible || templatePlayerVisible || currentIcon === 'subtitle' || currentIcon === 'videoscript' || !!clippingEffect
    },
    locked () {
      return this.editLock || this.ownLock
    },
    liFisherActive () {
      return !!(this.userBasics && this.userBasics.origin_id)
    },
    coverAreaStyle () {
      const { selectionArea } = this
      return {
        top: selectionArea.y + 'px',
        left: selectionArea.x + 'px',
        width: selectionArea.w + 'px',
        height: selectionArea.h + 'px'
      }
    },
    canMute () {
      return this.allVideos.length > 0
    },
    allMuted: {
      get () {
        return this.allVideos.every(ef => {
          const efc = ef.content
          return efc.volume === 0
        })
      },
      set (value) {
        this.allVideos.forEach(ef => {
          const efc = ef.content
          efc.volume = value ? 0 : 100
        })
        message.info(value ? '视频原声已关闭。' : '视频原声已启用。')
      }
    }
  },
  methods: {
    ...mapMutations(['handleEffectSelect', 'handlePlayerScale', 'handleCurrentSubmenu', 'handleAudioSelect']),
    handleKeyDown (e) {
      // console.log(e);
      const propLookup = {
        37: ['left', -1],
        38: ['top', -1],
        39: ['left', 1],
        40: ['top', 1]
      }
      const ctrlKey = isMac && e.metaKey || e.ctrlKey
      if (!/input|textArea/i.test(e.target.tagName) && !this.contextMenuVisible && !this.playerVisible) {
        if (this.effectReordering && e.keyCode !== 32) {
          // space is handled elsewhere
          if (!this.keydownLookup[e.keyCode]) {
            this.keydownLookup[e.keyCode] = 1
            message.error({
              content: '排序时无法进行操作。',
              key: 'reorderkeydown'
            })
          }
          return
        }
        switch (e.keyCode) {
          // 删除键和 或 backspace键删除元素
          case 46:
          case 8:
            e.preventDefault()
            this.$store.dispatch('autoRemoveSelection')
            break
          case 37:
          case 38:
          case 39:
          case 40:
            if (this.selectedEffects.length) { // TODO: multiple selection.
              const { effects } = this.$store.state
              const arr = []
              this.selectedEffects.forEach(ef => {
                const efc = ef.content
                const prop = propLookup[e.keyCode]
                e.preventDefault()
                this.$set(efc, prop[0], efc[prop[0]] + prop[1])

                if (ef.container) {
                  if (effects[ef.container].type === 'group') {
                    arr.push(effects[ef.container])
                  }
                }
              })
              this.$store.dispatch('adjustGroupSize', arr)
            }
            break
          case 65:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('selectAll')
            }
            break
          case 67:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('copyEffect')
            }
            break
          case 68:
            if (ctrlKey) {
              const ef = this.$store.getters.selectedEffect
              if (ef) {
                switch (ef.type) {
                  case 'image':
                  case 'video':
                    e.preventDefault()
                    this.$store.commit('handleFilterShowing', false)
                    this.$store.dispatch('setEditStatus', true)
                    break
                  case 'container':
                  case 'textContainer':
                    e.preventDefault()
                    this.$store.commit('handleContainerEdit', ef.id)
                    this.$store.commit('handleCurrentIcon', 'material')
                    this.$store.commit('handleCurrentMenu', 'MaterialMenu')
                    break
                }
              }
            }
            break
          case 71:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('groupEffects')
            }
            break
          case 83:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('saveDraftNotify')
            }
            break
          case 86:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('pasteEffect')
            }
            break
          case 88:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('cutEffect')
            }
            break
          case 89:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('redo')
            }
            break
          case 90:
            if (ctrlKey) {
              e.preventDefault()
              this.$store.dispatch('undo')
            }
            break
          case 222:
            if (ctrlKey) {
              e.preventDefault()
              this.toggleGridShow()
            }
            break
        }
      }
    },
    handleKeyUp (e) {
      console.log('keyup')
      delete this.keydownLookup[e.keyCode]
    },
    toggleGridShow () {
      this.$store.commit('handleGridShow', !this.gridShowing)
    },
    scrollTo (to) {
      const { menu } = this.$refs
      menu.scrollTop = to
    },
    handleMouseWheel (e) {
      if (this.selectionDrawing) {
        e.preventDefault()
        return
      }
      if (e.ctrlKey) {
        e.preventDefault()

        if (e.deltaY > 0) {
          this.handlePlayerScale(clamp(this.playerScale - 10, 50, 200))
        } else {
          this.handlePlayerScale(clamp(this.playerScale + 10, 50, 200))
        }
      }
    },
    handlePlayerClick () {
      if (!this.selectedEffectId) {
        if (this.keyboardShortcutShowing) {
          this.$store.commit('handleKeyboardShortcutShow', false)
        }

        if (this.selectedAudioId) {
          this.$store.commit('handleAudioSelect', null)
        }
      }
      if (this.currentSubmenu === 'BackgroundAudioFormat') {
        this.handleCurrentSubmenu('VideoFormat')
      }
      this.handleEffectSelect([])
    },
    handleMouseDown (e) {
      if (e.button) {
        return
      }
      e.preventDefault()
      if (!this.playing) {
        this.$store.commit('handleSelectionDraw', true)
        this.$store.commit('handleEffectPreselect', [])
        this.$store.commit('handleSelectionArea', {
          x: e.clientX,
          y: e.clientY,
          w: 0,
          h: 0
        })
      }
    },
    handleMouseMove (e) {
      if (this.selectionDrawing) {
        const { x, y } = this.origSelectionPos
        const deltaX = e.clientX - x
        const deltaY = e.clientY - y
        const next = {
          x,
          y,
          w: deltaX,
          h: deltaY
        }
        if (deltaX < 0) {
          next.w = -deltaX
          next.x += deltaX
        }
        if (deltaY < 0) {
          next.h = -deltaY
          next.y += deltaY
        }

        this.$store.dispatch('markIntersecting', next)
      }
    },
    handleMouseUp () {
      if (this.selectionDrawing) {
        this.$store.dispatch('selectIntersecting')
      }
    },
    openBackgroundAudio () {
      this.$store.commit('handleKeyboardShortcutShow', false)
      this.handleEffectSelect([])
      this.$store.commit('handleAudioSelect', null)

      this.$nextTick(() => {
        this.handleCurrentSubmenu('BackgroundAudioFormat')
      })
    },
    toggleVideoScriptPeek () {
      this.$store.commit('handleVideoScriptPeekerVisible', !this.videoScriptPeekerVisible)
    },
    toggleMenuCollapse () {
      this.$store.commit('handleMenuCollapse', !this.menuCollapsed)
    },
    muteAll () {
      this.allMuted = !this.allMuted
    },
    toggleSnapEnable () {
      this.$store.commit('handleSnapEnable', !this.snapEnabled)
    },
    async checkFisher (init = false) {
      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)
      const token = params.get('token') || localStorage.getItem('token') || ''
      const regex = /-fishId(\d+)/
      const match = token.match(regex)
      if (token && match) {
        window.localStorage.clear()
        localStorage.setItem('token', token)
        console.log('fisher token', token)
        Cookies.set('token', btoa(token))
        const link = document.querySelector("link[rel~='icon']")
        if (link) {
          link.href = '/assets/imgs/favicon-lifisher.ico'
        }
        this.$store.state.isFisher = true
        if (params.get('type')) {
          this.$store.state.fisherInfo = {
            type: params.get('type')
          }
        }
        if (init) return
        const b = new URL(location.href)
        b.searchParams.delete('token')
        history.replaceState(null, '', b.href)
        const url = process.env.NODE_ENV === 'production' ? '/api/tokenlogin' : 'http://test.aivideo8.com/api/tokenlogin'
        const loginRes = await axios.post(
          url,
          { token })
        console.log('loginRes', loginRes)
        const { status } = loginRes.data
        if (status === 1) {
          console.log(localStorage.getItem('token'))
          Cookies.set('token', btoa(token))
          console.log('store', this.$store)
          this.$store.dispatch('getLiFisherSites')
          const res = await get('loginlog/login-info')
          console.log('loginlog res', res)
          this.$store.state.isFisher = true
          console.log('params', params)
          console.log('params', params.get('type'))
          if (params.get('type')) {
            res.type = params.get('type')
            res.videoListURL += '&type=digitalHuman'
          }
          this.$store.state.fisherInfo = res
          sessionStorage.setItem('fisherInfo', JSON.stringify(res))
          if (res.videoListURL) {
            this.$store.commit('handleBackUrl', decodeURIComponent(res.videoListURL))
          } else {
            this.$store.commit('handleBackUrl', decodeURIComponent(res.backURL))
          }
        }
      }
    },
    async setTackId () {
      if (this.$store.state.trackId) return
      const params = {
        module_name: 'video'
      }
      if (this.isFisher && this.fisherInfo?.type === 'digitalHuman') {
        params.module_name = 'dp.video'
      }
      const res = await post('get-new-trackid',params)
      console.log('track id res', res);
      this.$store.state.trackId = res.track_id;
    },
    async init () {
      await this.checkFisher()
      this.keydownLookup = {}
      document.body.addEventListener('keydown', this.handleKeyDown)
      document.body.addEventListener('keyup', this.handleKeyUp)

      document.body.addEventListener('mousemove', this.handleMouseMove)
      document.body.addEventListener('mouseup', this.handleMouseUp)

      let browserUpdated = false

      navigator.userAgent.replace(/chrome\/(\d+)/i, (_, v) => {
        // do not change this version number.
        if (+v >= 75) {
          browserUpdated = true
        }
      })
      // they are not compatible with current features after all. do not add other browsers here.
      // do not change this version number.
      if (+localStorage.getItem('ignoreBrowserUpdate') >= 75) {
        browserUpdated = true
      }

      if (!browserUpdated) {
        let ignore = false
        const modalContent = h => [
          h('p', {}, ['为保障优质的使用体验及动态效果，建议您使用版本 75 以上的 Google Chrome 浏览器。']),
          h('a-checkbox', {
            attrs: {
              checked: ignore
            },
            on: {
              change (e) {
                ignore = e.target.checked

                modal.update({
                  content: modalContent
                })
              }
            }
          }, '下次不再提示')
        ]
        const modal = Modal.warning({
          title: '提示',
          content: modalContent,
          onOk () {
            if (ignore) {
              console.log('Ignore browser update notice')
              try {
                // do not change this version number.
                localStorage.setItem('ignoreBrowserUpdate', '75')
              } catch (e) {
                console.log('Cannot set localStorage')
              }
            }
          }
        })
      }

      const externalInit = [
        { search: 'id', key: 'id' },
        { search: 'draftid', key: 'draftId' },
        { search: 'edittemplateid', key: 'templateId', type: 'template' },
        { search: 'backup', key: 'backup' },
        { search: 'templateid', key: 'templateId', params: ['type'] }
      ].some(item => {
        let done = false
        console.log(JSON.stringify(item))
        window.location.search.replace(
          new RegExp(`[?&]${item.search}=([^&]+)`, 'i'),
          (_, value) => {
            if (value) {
              const type = item.type
              let params
              if (item.params) {
                params = {}
                item.params.forEach(p => {
                  window.location.search.replace(
                    new RegExp(`[?&]${p}=([^&]+)`, 'i'), (_, val) => {
                      params[p] = val
                    })
                })
              }
              this.$store.dispatch('loadDraftData', {
                [item.key]: value,
                type,
                params
              })
              done = true
            }
          }
        )
        return done
      })

      window.location.search.replace(
        /[?&]back=([^&]+)/,
        (_, value) => {
          if (value) {
            console.log('backurl', value)
            this.$store.commit('handleBackUrl', decodeURIComponent(value))
          }
        }
      )

      window.location.search.replace(
        /[?&]readonly=([^&]+)/,
        (_, value) => {
          if (value) {
            this.$store.commit('handleReadonly', true)
            message.info('当前视频以只读方式打开。')
          }
        }
      )

      if (!externalInit) {
        // TODO:
        this.$store.dispatch('initUndoStack')
        this.$nextTick(() => {
          this.$store.commit('handleLoading', false)
        })
      }

      this.$store.dispatch('loadFontList')
      // this.$store.dispatch('getLiFisherSites');

      this.$store.dispatch('loadShapeList')
      this.$store.dispatch('loadContainerList')
      this.$store.dispatch('loadGeneralEffectList')
      this.$store.dispatch('loadTextEffectList')
      this.$store.dispatch('loadTransitionList')

      this.$store.dispatch('getUserMeal')
      this.$store.dispatch('getUserBasics')
      this.$store.dispatch('getTagData')
      this.$store.dispatch('getVideoScriptSceneTypes')
      this.$store.dispatch('getUploadSpace')
      this.$store.dispatch('getMusicCategoryList')
      this.$store.dispatch('getTemplateSceneIndustryList')
      this.setTackId().then()
      initWebSocket()
    }
  },
  watch: {
    showTooltipMarker (val) {
      if (this.hideTooltipTimeout) {
        clearTimeout(this.hideTooltipTimeout)
      }
      if (val) {
        this.showTooltip = true
        this.hideTooltipTimeout = setTimeout(() => {
          this.hideTooltipTimeout = null
          this.showTooltip = false
        }, 3e3)
      } else {
        this.hideTooltipTimeout = null
        this.showTooltip = false
      }
    },
    params: {
      handler () {
        if (!this.recordDisabled) {
          if (this.locked) {
            this.hasUndo = true
          } else {
            console.log('params changed')
            this.$store.dispatch('recordUndoStack')
            this.ownLock = setTimeout(() => {
              this.ownLock = false
            }, 500)
          }
        }
      },
      deep: true
    },
    locked (val) {
      if (!val && this.hasUndo) {
        this.hasUndo = false
        console.log('params changed from locked watcher')
        this.$store.dispatch('recordUndoStack')
      }
    },
    multipleSelected (val) {
      if (val) {
        this.$store.commit('handleKeyboardShortcutShow', false)
      }
    },
    liFisherActive () {
      this.$store.dispatch('getLiFisherSites')
    },
    loading (val) {
      if (!val && !this.initialized) {
        const wait = Math.max(0, loadStart + 2e3 - new Date())
        console.log(wait)
        setTimeout(() => {
          this.$store.commit('handleInitialize', true)
        }, wait)
      }
    },
    async usedTemplateId (val) {
      const alts = {}
      if (val) {
        const resp = await get('template/relation', { id: val })
        alts.name = resp.name
        resp.category_list.forEach(c => {
          alts[c.specs] = c
        })
        console.log(alts)
      }
      this.$store.commit('handleAlternateTemplates', alts)
    },
    stampPadding (val) {
      this.stamp.offset = clamp(this.stamp.offset, 10, val * 3)
    },
    selectionDrawing (val) {
      if (val) {
        const { x, y } = this.selectionArea
        this.origSelectionPos = { x, y }
      } else {
        setTimeout(() => {
        }, 0)
      }
    }
  },
  mounted () {
    console.log('mounted')
    this.init()
  },
  created () {
    console.log('created')
    this.checkFisher(true)
  },
  beforeDestroy () {
    document.body.removeEventListener('keydown', this.handleKeyDown)
    document.body.removeEventListener('keyup', this.handleKeyUp)
  }
}
</script>

<style>
body,
.ant-tabs,
.ant-checkbox-wrapper {
  color: rgba(0, 0, 0, 0.75);
}

.el-tooltip__popper {
  line-height: 1.4;
  padding: 8px 10px;
}

.ant-modal-confirm .ant-modal-body {
  padding: 20px 28px 24px;
}

.ant-modal-confirm-body > .anticon {
  position: absolute;
  top: 61px;
  font-size: 21px;
}

.ant-modal-confirm-body .ant-modal-confirm-content {
  margin-top: 18px;
}

.unselectable {
  user-select: none;
}

.effect-locked {
  pointer-events: none;
}

.effect-filter-cover:after {
  content: "";
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  backdrop-filter: var(--filter, none);
  z-index: 1;
  position: absolute;
}

.preview-app {
  height: 100%;
  min-width: 1366px;
  background: rgba(231, 232, 233, 1);
}

.preview-app > .ant-spin-container {
  padding-top: 50px;
  height: 100%;
}

.preview-app--initial > .ant-spin-blur,
.preview-app--initial > .ant-spin-blur:after {
  opacity: 1;
}

.preview-app > .ant-spin-blur:after {
  z-index: 101;
}

.preview-app > div > .ant-spin {
  z-index: 102;
}

.preview-app--initial > div > .ant-spin {
  max-height: none;
}

.preview-app--initial > div > .ant-spin .ant-spin-dot {
  width: 100px;
  height: 100px;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  background: #fff
    url(https://img001.aivideo8.com/ai_video/U449/2021-07-08/img/1625726499432.gif)
    center no-repeat;
  background-size: 90px;
}

.preview-main {
  display: flex;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.app-player-format {
  display: flex;
  flex-grow: 1;
  transition: margin 0.2s;
}

.preview-app--collapsed .app-player-format {
  margin-left: -280px;
}

.app-player {
  flex: 1;
  /* padding-bottom: 200px; */
  overflow: auto;
  overflow-y: scroll;
  text-align: center;
  position: relative;
  z-index: 3;
  max-width: calc(100vw - 636px);
  min-width: 734px;
  white-space: nowrap;
  transition: 0.2s;
  padding: 0 20px;
}

.preview-app--collapsed .app-player {
  max-width: calc(100vw - 356px);
}

.app-player-format__actions {
  width: 40px;
  background: rgba(255, 255, 255, 0.45);
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: -1px 0 rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.app-player-format__action-divider {
  width: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin: 12px auto;
}

.app-player-format__action {
  width: 30px;
  height: 30px;
  border: 0;
  outline: 0;
  border-radius: 2px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 5px auto 0;
  color: rgba(0, 0, 0, 0.3);
  background: transparent;
  transition: 0.3s;
  cursor: pointer;
}

.app-player-format__action--small {
  font-size: 14px;
}

.app-player-format__action:hover,
.app-player-format__action:focus {
  background-color: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.4);
}

.app-player-format__action:disabled {
  opacity: 0.5;
  background-color: transparent;
  color: rgba(0, 0, 0, 0.3);
}

.app-player-format__action--active,
.app-player-format__action--active:hover,
.app-player-format__action--active:focus {
  background: #fff;
  color: #007eff;
}

.app-player:before {
  content: "";
  height: 100%;
  vertical-align: middle;
  display: inline-block;
}

.app-info-component-wrapper {
  overflow: auto;
  width: 256px;
  background: #f8f8f8;
}

.app-timeline {
  background: #fff;
  position: fixed;
  bottom: 0;
  left: 340px;
  right: 0;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08), 0 -1px 0 rgba(0, 0, 0, 0.02);
  transition: left 0.2s;
}

.preview-app--collapsed .app-timeline {
  left: 60px;
}

/* Slider 滑动条 */
.ant-slider-rail {
  background: rgba(0, 0, 0, 0.05);
}

.ant-slider-rail,
.ant-slider-track,
.ant-slider-step {
  height: 2px;
}

.ant-slider-handle {
  width: 12px;
  height: 12px;
}

.ant-slider-track,
.ant-slider:hover .ant-slider-track {
  background-color: #007eff;
}

.ant-slider-handle,
.ant-slider:hover .ant-slider-handle:not(.ant-tooltip-open) {
  border: solid 2px #007eff;
}

.el-slider__button-wrapper,
.el-slider__button {
  outline: 0;
}

.menu-enter-active,
.menu-leave-active {
  transition: all 0.2s ease-in-out;
}

.menu-enter,
.menu-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.sub-menu-enter-active,
.sub-menu-leave-active {
  transition: 0.2s;
}

.sub-menu-enter,
.sub-menu-leave-to {
  opacity: 0;
}

.fragment-sub-menu.sub-menu-enter,
.fragment-sub-menu.sub-menu-leave-to {
  transform: rotateY(90deg);
}

.preview-app__menu,
.fragment-sub-menu {
  flex-shrink: 0;
  background: rgba(247, 248, 249, 1);
}

.preview-app__menu {
  width: 280px;
  outline: 0;
  position: relative;
  overflow: hidden;
  transition: 0.2s;
}

.preview-app__menu--top,
.fragment-sub-menu {
  z-index: 3;
}

.preview-app--collapsed .preview-app__menu {
  opacity: 0;
  visibility: hidden;
  transform: translate3d(-100%, 0, 0);
}

.fragment-sub-menu {
  position: absolute;
  left: 340px;
  z-index: 4;
  width: 390px;
  background: rgba(247, 248, 249, 1);
  box-shadow: 0 12px 48px 16px rgba(0, 0, 0, 0.03),
    0 9px 28px 0 rgba(0, 0, 0, 0.05), 0 6px 16px -8px rgba(0, 0, 0, 0.08);
  border-radius: 2px;
  height: 100%;
  overflow: hidden;
  transform-origin: center left;
  transition: 0.2s;
}

.preview-app--collapsed .fragment-sub-menu {
  left: 60px;
}

.menu-collapse-btn {
  position: absolute;
  left: 340px;
  z-index: 4;
  bottom: 50%;
  border: 0;
  outline: 0;
  background: transparent;
  border-radius: 0;
  width: 16px;
  height: 36px;
  cursor: pointer;
  background: #f7f8f9;
  transition: 0.2s;
  padding: 0 2px 0 0;
}

.preview-app--collapsed .menu-collapse-btn {
  left: 60px;
}

.menu-collapse-btn--sub {
  left: 730px;
}

.preview-app--collapsed .menu-collapse-btn--sub {
  left: 450px;
}

.menu-collapse-btn:before,
.menu-collapse-btn:after {
  content: "";
  position: absolute;
  left: 0;
  transition: 0.2s;
  border-left: 16px solid #f7f8f9;
}

.menu-collapse-btn:before {
  bottom: 100%;
  border-top: 9px solid transparent;
}

.menu-collapse-btn:after {
  top: 100%;
  border-bottom: 9px solid transparent;
}

.menu-collapse-btn:hover,
.menu-collapse-btn:focus {
  background-color: #dedfe0;
}

.menu-collapse-btn:hover:before,
.menu-collapse-btn:focus:before,
.menu-collapse-btn:hover:after,
.menu-collapse-btn:focus:after {
  border-left-color: #dedfe0;
}

.menu-collapse-btn__svg {
  display: block;
  margin: 0 auto;
  position: relative;
}

.menu-collapse-btn__path {
  transition: d 0.3s;
  fill: rgba(0, 0, 0, 0.2);
}

.material-tooltip .popper__arrow {
  top: 50% !important;
  margin-top: -6px;
}

.preview-app__cover {
  position: fixed;
  background: rgba(0, 0, 0, 0.08);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
  z-index: 4;
  pointer-events: none;
}

::-webkit-scrollbar {
  width: 16px;
  height: 16px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  border: 4px solid transparent;
  background-color: rgba(0, 0, 0, 0.12);
  background-clip: content-box;
  border-radius: 8px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.2);
}

::-webkit-scrollbar-corner {
  background: transparent;
}

/* 片段预览定位 */
.template-preview-material {
  position: absolute;
  width: 500px;
  height: 280px;
  left: 730px;
  top: 0;
  z-index: 4;
  transition: 0.2s;
}

.preview-app--collapsed .template-preview-material {
  left: 450px;
}

.u-bg-center-cover {
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.upload_dialog_container .ant-modal-mask,
.upload_dialog_container .ant-modal-wrap,
.image-crop-modal .ant-modal-mask,
.image-crop-modal .ant-modal-wrap {
  z-index: 1002;
}

.modal-upload-confirm-dlg .ant-modal-mask,
.modal-upload-confirm-dlg .ant-modal-wrap {
  z-index: 1003;
}
</style>
